import { useTranslation } from "react-i18next";
import { Button, Flex, Row, Space, Spin } from "antd";

import { useSelector } from "react-redux";
import styles from "../styles/Employee.module.css";

import { ReactComponent as DownloadIcon } from "../../../assets/icon/downloadIcon.svg";
import { useEffect, useRef, useState } from "react";
import { OrgChart } from "d3-org-chart";
import * as d3 from "d3";
import { toJpeg } from "html-to-image";

function EmployeeEmploymentSupervisor({
  isLoading = false,
  graphRef = null
}) {
  const { t, i18n } = useTranslation();
  const supervisorList = useSelector((state) => state.employee.supervisorList);
  const subOrdinate = useSelector((state) => state.employee.subOrdinateDetails);
  const [chart, setChart] = useState(null);
  const chartRef = useRef(null);

  const downloadImage = async () => {
    const chartElement = document.getElementById('employeeEmploymentSupervisor');

    if (!chartElement) {
      console.error('Element with id "employeeEmploymentSupervisor" not found');
      return;
    }

    const originalWidth = chartElement.style.width;
    const originalHeight = chartElement.style.height;

    chartElement.style.width = `${chartElement.scrollWidth}px`;
    chartElement.style.height = `${chartElement.scrollHeight}px`;

    const scale = 10;
    try {
      const dataUrl = await toJpeg(chartElement, {
        width: chartElement.scrollWidth * scale,
        height: chartElement.scrollHeight * scale,
        backgroundColor: "white",
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${chartElement.scrollWidth}px`,
          height: `${chartElement.scrollHeight}px`,
        },
      });

      chartElement.style.width = originalWidth;
      chartElement.style.height = originalHeight;

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'organizational-chart.jpg';
      link.click();

    } catch (error) {
      console.error('Could not generate image', error);
    }
  };

  const handleDownloadClick = async () => {
    try {
      let res = await chart?.compact?.(false).render().fit().render();
      if (res) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        downloadImage();
      }
    } catch (error) {
      console.error('Error during download:', error);
    }
  };

  function transformData(data, lang) {

    if (!Array.isArray(supervisorList) || supervisorList.length === 0 || !data?.empId) {
      return [];
    }

    const supervisor = supervisorList[0];

    const result = [
      {
        id: supervisor?.empId || null,
        preventExpand: true
        , parentId: null,
        name: supervisorList[0]?.['name' + (lang == 'en' ? "En" : "")] + " " + supervisorList[0]?.['surName' + (lang == 'en' ? "En" : "")],
        position: supervisorList[0]?.['positionName' + (lang == 'en' ? "En" : "")] || "",
      },
      {
        id: data.empId,
        preventExpand: true
        , parentId: supervisor?.empId || null,
        name: data?.['name' + (lang == 'en' ? "En" : "")] + " " + data?.['surName' + (lang == 'en' ? "En" : "")],
        position: data?.['positionName' + (lang == 'en' ? "En" : "")] || "",
      }
    ];
    console.log("result", result);
    return result;
  }

  useEffect(() => {
    const chartContainer = chartRef?.current;

    if (!chartContainer && !subOrdinate && !supervisorList) {
      console.error("Chart container is not available.");
      return;
    }
    const orgChart = new OrgChart()
      .nodeHeight((d) => 50 + 25)
      .nodeWidth((d) => 300 + 2)
      .childrenMargin((d) => 150)
      .compactMarginBetween((d) => 200)
      .compactMarginPair((d) => 200)
      .neighbourMargin((a, b) => 200)
      .buttonContent(() => { })
      .onNodeClick((d) => console.log(d))
      .nodeContent(function (d) {
        const color = '#FFFFFF';
        const imageDiffVert = 25 + 2;
        return `
              <div id='node-na' style="width:${d.width}px;height:${d.height}px;padding-left:1px;padding-right:1px;">
                <div style="background-color:${color};margin-left:-1px;width:${d.width - 2}px;border-radius:100px;border: 1px solid black ;padding-left :20px;padding-right :20px;">
                  <div style="min-height:${d.height - 20}px;align-content:center;font-size:1.3vw;color:#08011E;text-align:center ;white-space: normal; word-wrap: break-word; overflow: hidden; text-overflow: ellipsis;">${d.data?.name}</div>
                  <div style="margin-bottom:15px;align-content:center;font-size:1vw;color:#08011E;text-align:center">${"(" + d.data?.position + ")"}</div>
                </div>
              </div>`;
      })
      .container(chartContainer)
      .data(transformData(subOrdinate, i18n.language))
      .render();

    const svg = d3.select(chartContainer).select("svg");

    if (svg) {

      svg.selectAll(".node")
        .style("cursor", "default");
      svg.selectAll("foreignObject").remove()
    }
    orgChart?.expandAll()
    orgChart?.compact?.(false).render();
    orgChart?.nodeButtonWidth((node) => node.data?.preventExpand ? 0 : 20)
      .nodeButtonHeight((node) => node.data?.preventExpand ? 0 : 20);
    const rootNodeId = subOrdinate[0]?.empId;
    if (rootNodeId) {
      orgChart.setCentered(rootNodeId);
    }

    const adjustChartSize = () => {
      orgChart.fit();
    };
    setChart(orgChart);
    return () => {
      if (chart) {
        chart?.destroy?.();
      }
    };
  }, [subOrdinate, supervisorList, i18n.language]);

  useEffect(() => {
    if (graphRef?.current) {
      graphRef.current = chart
    }
  }, [chart])

  if (!supervisorList || supervisorList.length === 0 || !subOrdinate || subOrdinate.length === 0) {
    return null;
  }
  return (
    <Space style={{ width: "100%" }} direction="vertical">
      <Spin spinning={isLoading}>
        <Space direction="vertical" size={24} style={{ width: "100%" }}>

          <Row justify="end">
            <Button type="default" disabled={isLoading} onClick={handleDownloadClick}
              style={{ padding: "8px 16px", height: "max-content", borderRadius: "8px" }}
            >
              <Flex align="center" justify="center" gap={8}>
                <DownloadIcon />
                <span style={{ color: "#667085", fontWeight: "bold" }}>{t('whiteList..download')}</span>
              </Flex>
            </Button>
          </Row>

          <div>
            <div className={styles.stylesNode2}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px', gap: "12px" }}>
                <Button onClick={() => {
                  chart.nodeHeight(() => 125 + 25);
                  chart.layout('left').render().fit()
                }}>
                  {t("og_chart.left")}
                </Button>
                <Button onClick={() => {
                  chart.nodeHeight(() => 50 + 25);
                  chart.layout('top').render().fit()
                }}>
                  {t("og_chart.top")}
                </Button>
              </div>
              <div id="employeeEmploymentSupervisor" style={{ width: "100%" }} ref={chartRef} className="chart-container"></div>
            </div>
          </div>

        </Space>
      </Spin>
    </Space>
  );
}

export default EmployeeEmploymentSupervisor;
